import i18n from '../../../i18n/i18n';
import type { PendingFriendRequest, PlayersDirectoryItem } from '../../../services/api';

/**
 * The Friends tab's row model, fed by gateway-game `/api/friends`. The Figma "Friends list"
 * (4244:41103) also draws "In Game" and "Offline • 2 hours ago"; the directory only knows online
 * or not, so those two stay in the type for when presence carries them.
 */
export type FriendStatus =
	| { kind: 'online' }
	| { kind: 'in-game' }
	/** The design spells out how long ago, so the copy travels with the row when it is known. */
	| { kind: 'offline'; since?: string };

export type Friend = {
	id: string;
	username: string;
	level: number;
	avatarFileId: string | null;
	status: FriendStatus;
};

export const friendFromDirectory = (item: PlayersDirectoryItem): Friend => ({
	id: item.id,
	username: item.username ?? '-',
	level: item.level,
	avatarFileId: item.avatarFileId,
	status: item.isOnline ? { kind: 'online' } : { kind: 'offline' },
});

/** A request row carries the requester; it has no presence, so it reads as the request itself. */
export const friendFromRequest = (request: PendingFriendRequest): Friend => ({
	id: request.requesterId,
	username: request.requesterUsername ?? '-',
	level: request.requesterLevel,
	avatarFileId: request.requesterAvatarFileId,
	status: { kind: 'offline' },
});

/** Resolved when read, never at module load - translations arrive from the backend after start-up. */
export const friendsSearchPlaceholder = () =>
	i18n.t('T_SITE_FRIENDS_SEARCH_PLACEHOLDER', { defaultValue: 'Search friends by username…' });

/** The status line under a username, as the design writes it. */
export const friendStatusLabel = (status: FriendStatus) => {
	switch (status.kind) {
		case 'online':
			return i18n.t('T_SITE_FRIENDS_STATUS_ONLINE', { defaultValue: 'Online' });
		case 'in-game':
			return i18n.t('T_SITE_FRIENDS_STATUS_IN_GAME', { defaultValue: 'In Game' });
		case 'offline':
			return status.since
				? i18n.t('T_SITE_FRIENDS_STATUS_OFFLINE_SINCE', {
						defaultValue: 'Offline • {{since}}',
						since: status.since,
					})
				: i18n.t('T_SITE_FRIENDS_STATUS_OFFLINE', { defaultValue: 'Offline' });
	}
};
